import React, { useEffect, useState } from 'react'
import { useStudent } from '@hooks/useStudent'
import {
  HEADING_TYPE,
  TEXT_SIZES,
  DIVIDER_SIZE,
  CONTAINER_STYLE,
  BUTTON_TYPE,
  BUTTON_SIZE,
} from '@helpers/constants'
import Container from '@components/Container'
import Text from '@components/Text'
import Loader from '@components/Loader'
import StudentBookList from '@components/StudentBookList'
import Heading from '@components/Heading'
import Divider from '@components/Divider'
import { groupBy } from '@helpers/utilities'
import { getSubjectTextClass } from '@helpers/subject'
import { useItemSettings } from '@hooks/useItemSettings'
import { useExamsAllowed } from '@hooks/useExamsAllowed'
import Button from '@components/Button'

const StudentBooks = () => {
  const [allBooks, setAllBooks] = useState([])

  const [groupedBooks, setGroupedBooks] = useState({})
  const { student, error, isLoading } = useStudent()
  const { subjects } = useItemSettings()

  const [showArchived, setShowArchived] = useState(false)

  const examsAllowed = useExamsAllowed()

  // gather up shared books and decorate them with customer (teacher)
  useEffect(() => {
    if (student) {
      const books = [
        ...student.schoolClasses.flatMap((sc) => sc.books),
        ...student.schoolGroups.flatMap((sg) => sg.books),
      ].reduce(
        (acc, book) =>
          acc.every((unique) => unique.id !== book.id) ? [...acc, book] : acc,
        []
      )

      setAllBooks(books)

      const currentBooks = books.filter(
        (book) => book.pivot?.is_archived !== true
      )
      const archivedBooks = books.filter(
        (book) => book.pivot?.is_archived === true
      )

      const booksToShow = !examsAllowed
        ? books
        : showArchived
        ? archivedBooks
        : currentBooks

      setGroupedBooks(groupBy(booksToShow, 'subject', 'id'))
    }
  }, [student, examsAllowed, showArchived])

  return (
    <Container size={CONTAINER_STYLE.DESKTOP}>
      <div className={'flex flex-col items-center justify-center'}>
        {isLoading ? (
          <Loader />
        ) : (
          <>
            {error && <Text>Nåt gick fel när böcker skulle laddas.</Text>}

            {allBooks.length === 0 && (
              <Text>Din lärare har inte delat någon bok med dig än.</Text>
            )}

            {examsAllowed && (
              <div className='mb-6'>
                <Button
                  type={
                    !showArchived ? BUTTON_TYPE.PRIMARY : BUTTON_TYPE.SECONDARY
                  }
                  size={BUTTON_SIZE.MEDIUM}
                  icon={'Star'}
                  iconLeft
                  margin
                  handleClick={() => {
                    setShowArchived(false)
                  }}
                >
                  Aktuella
                </Button>

                <Button
                  type={
                    showArchived ? BUTTON_TYPE.PRIMARY : BUTTON_TYPE.SECONDARY
                  }
                  size={BUTTON_SIZE.MEDIUM}
                  icon={'FileCabinet'}
                  iconLeft
                  margin
                  handleClick={() => {
                    setShowArchived(true)
                  }}
                >
                  Arkiverade
                </Button>
              </div>
            )}

            {!Object.keys(groupedBooks).length && (
              <>
                {showArchived ? (
                  <Text>Inga arkiverade böcker att visa.</Text>
                ) : (
                  <Text>Inga aktuella böcker att visa.</Text>
                )}
              </>
            )}

            <div className={'w-full'}>
              {groupedBooks &&
                Object.keys(groupedBooks).map((subject) => (
                  <div key={subject}>
                    <Heading
                      color={getSubjectTextClass(
                        subjects?.find((s) => s.id === subject)?.slug,
                        'dark'
                      )}
                      size={TEXT_SIZES.XXLARGE}
                      headingLevel={HEADING_TYPE.H1}
                    >
                      {groupedBooks[subject].name}
                    </Heading>

                    <Divider size={DIVIDER_SIZE.SMALL} />
                    <StudentBookList
                      items={groupedBooks[subject].items}
                      rowLimit={2}
                    />
                    <Divider size={DIVIDER_SIZE.BASE} />
                  </div>
                ))}

              <Divider size={DIVIDER_SIZE.BASE} />
            </div>
          </>
        )}
      </div>
    </Container>
  )
}

export default StudentBooks
